<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useSmartCampaignsStore } from 'dashboard/stores/smartCampaigns';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SmartCampaignDialog from 'dashboard/components-next/SmartCampaigns/SmartCampaignDialog.vue';
import EditSmartCampaignDialog from 'dashboard/components-next/SmartCampaigns/EditSmartCampaignDialog.vue';
import ConfirmDeleteSmartCampaignDialog from 'dashboard/components-next/SmartCampaigns/ConfirmDeleteSmartCampaignDialog.vue';
import SmartCampaignList from 'dashboard/components-next/SmartCampaigns/SmartCampaignList.vue';
import SmartCampaignEmptyState from 'dashboard/components-next/SmartCampaigns/SmartCampaignEmptyState.vue';

const { t } = useI18n();
const store = useSmartCampaignsStore();

const createDialogRef = ref(null);
const editDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedCampaign = ref(null);

const campaigns = computed(() => store.getRecords);
const isFetching = computed(() => store.uiFlags.fetchingList);
const hasNoCampaigns = computed(
  () => campaigns.value.length === 0 && !isFetching.value
);

onMounted(() => store.get());

const openCreateDialog = () => createDialogRef.value?.dialogRef?.open();

const handleCreate = async details => {
  try {
    await store.create(details);
    useAlert(t('SMART_CAMPAIGNS.CREATE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('SMART_CAMPAIGNS.CREATE.API.ERROR_MESSAGE'));
  }
};

const handleEdit = campaign => {
  selectedCampaign.value = campaign;
  editDialogRef.value?.dialogRef?.open();
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  deleteDialogRef.value?.dialogRef?.open();
};

const handleToggleStatus = async campaign => {
  const newStatus = campaign.status === 'active' ? 'inactive' : 'active';
  try {
    await store.update({ id: campaign.id, status: newStatus });
    useAlert(t('SMART_CAMPAIGNS.TOGGLE_STATUS.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('SMART_CAMPAIGNS.TOGGLE_STATUS.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('SMART_CAMPAIGNS.HEADER') }}
          </span>
          <Button
            :label="t('SMART_CAMPAIGNS.NEW_CAMPAIGN')"
            icon="i-lucide-plus"
            size="sm"
            @click="openCreateDialog"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <div
        v-else-if="!hasNoCampaigns"
        class="w-full max-w-5xl mx-auto px-6 py-4"
      >
        <SmartCampaignList
          :campaigns="campaigns"
          @edit="handleEdit"
          @delete="handleDelete"
          @toggle-status="handleToggleStatus"
        />
      </div>
      <SmartCampaignEmptyState
        v-else
        :title="t('SMART_CAMPAIGNS.EMPTY_STATE.TITLE')"
        :subtitle="t('SMART_CAMPAIGNS.EMPTY_STATE.SUBTITLE')"
      />
    </main>
  </section>

  <SmartCampaignDialog ref="createDialogRef" @submit="handleCreate" />
  <EditSmartCampaignDialog
    ref="editDialogRef"
    :selected-campaign="selectedCampaign"
  />
  <ConfirmDeleteSmartCampaignDialog
    ref="deleteDialogRef"
    :selected-campaign="selectedCampaign"
  />
</template>
